// src/utils/gates.ts
import type { User } from '../types';
import { track, Events } from './analytics';
import { supabase } from '../lib/supabaseClient';

export type GateType =
  | 'profile_completion'
  | 'verification'
  | 'premium_subscription';

export type GateResult = {
  allowed: boolean;
  gate?: GateType;
  title?: string;
  message?: string;
  actionText?: string;
  actionUrl?: string;
};

const isVipWindowActive = (u: any) => {
  if (!u?.vipLaunch || !u?.trialEndsAt) return false;
  const end = new Date(u.trialEndsAt).getTime();
  return Date.now() < end;
};

function trackGateBlock(gate: GateType, reason: string, userId?: string) {
  try {
    track?.(Events?.GateBlocked ?? 'gate_blocked', { gate, reason, userId });
  } catch {}
}

function ok(): GateResult {
  return { allowed: true };
}

/**
 * IMPORTANT:
 * - Viewing events should always be allowed.
 * - Interaction (request, host, message, connect) can be gated.
 */
export const buildGates = (user: User | null) => {
  // Admin bypass
  const isAdmin = user?.email === 'admin@vybetribe.app';
  const vipActive = isVipWindowActive(user);

  // Check if email is confirmed (from Supabase auth)
  const isEmailConfirmed = async () => {
    if (isAdmin) return true; // Admin bypass
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      return !!authUser?.email_confirmed_at;
    } catch {
      return false;
    }
  };
  
  // REMOVED: Photo requirement - users can explore without photo
  // Only check for interests as minimal requirement
  const hasCoreProfile =
    !!user &&
    Array.isArray(user.interests) &&
    user.interests.length > 0;

  const emailConfirmationGate = async (): Promise<GateResult> => {
    if (isAdmin) return ok(); // Admin bypass
    if (vipActive) return ok(); // VIP bypass
    const confirmed = await isEmailConfirmed();
    if (!confirmed) {
      return {
        allowed: false,
        gate: 'email_confirmation' as any,
        title: 'Confirm your email',
        message: 'Please check your email and click the confirmation link to continue.',
        actionText: 'Check email',
        actionUrl: '#', // Modal will handle this
      };
    }
    return { allowed: true };
  };

  const profileGate = (): GateResult => {
    if (isAdmin) return ok(); // Admin bypass
    if (vipActive) return ok(); // VIP bypass
    if (!hasCoreProfile) {
      trackGateBlock('profile_completion', 'profile_not_complete', user?.id);
      return {
        allowed: false,
        gate: 'profile_completion',
        title: 'Add interests to continue',
        message:
          'Pick a few interests to help us show you relevant Vybes.',
        actionText: 'Pick interests',
        actionUrl: '/onboarding',
      };
    }
    return { allowed: true };
  };

  const verificationGate = (): GateResult => {
    if (isAdmin) return ok(); // Admin bypass
    if (vipActive) return ok(); // VIP bypass
    const p = profileGate();
    if (!p.allowed) return p;

    // Allow verified users
    if (user?.isVerified) {
      return { allowed: true };
    }

    // Allow pending verifications - treat as verified until decision is made
    if (user?.verificationStatus === 'pending') {
      return { allowed: true };
    }

    // Block failed verifications
    if (user?.verificationStatus === 'declined' || user?.verificationStatus === 'failed') {
      trackGateBlock('verification', 'verification_failed', user?.id);
      return {
        allowed: false,
        gate: 'verification',
        title: 'Verification unsuccessful',
        message:
          'Your verification was not successful. Please try again or contact support.',
        actionText: 'Try again',
        actionUrl: '/verify',
      };
    }

    // Block unverified users who haven't started verification
    if (!user?.isVerified) {
      trackGateBlock('verification', 'not_verified', user?.id);
      return {
        allowed: false,
        gate: 'verification',
        title: 'Verify to interact',
        message:
          'Verification builds trust and enables requesting and hosting.',
        actionText: 'Verify now',
        actionUrl: '/verify',
      };
    }

    return { allowed: true };
  };

  const premiumGate = (): GateResult => {
    if (isAdmin) return ok(); // Admin bypass
    if (vipActive) return ok(); // VIP bypass
    const v = verificationGate();
    if (!v.allowed) return v;

    // Premium is no longer required for feature access
    // Only Vybe limits differ between free and subscribed users
    return { allowed: true };
  };

  // ✅ Allow everyone to view events (browsing is always allowed)
  // Verification only required for ACTIONS (apply, create, message)
  const canViewEvents = (): GateResult => {
    return ok(); // Always allow browsing
  };

  // ❗ One-shot helper to know why interactions are blocked
  const interactionBlocker = (): GateResult => {
    if (isAdmin) return ok(); // Admin bypass
    if (vipActive) return ok(); // VIP bypass
    const p = profileGate();
    if (!p.allowed) return p;
    const v = verificationGate();
    if (!v.allowed) return v;
    // Premium gate removed - only verification required
    return { allowed: true };
  };

  return {
    canViewEvents,
    canJoinEvent: async () => {
      if (isAdmin) return ok();
      if (vipActive) return ok();
      const emailCheck = await emailConfirmationGate();
      if (!emailCheck.allowed) return emailCheck;
      return verificationGate();
    },
    canMessage: async () => {
      if (isAdmin) return ok();
      if (vipActive) return ok();
      const emailCheck = await emailConfirmationGate();
      if (!emailCheck.allowed) return emailCheck;
      return verificationGate();
    },
    canConnect: async () => {
      if (isAdmin) return ok();
      if (vipActive) return ok();
      const emailCheck = await emailConfirmationGate();
      if (!emailCheck.allowed) return emailCheck;
      return verificationGate();
    },
    canHostEvent: async () => {
      if (isAdmin) return ok();
      if (vipActive) return ok();
      const emailCheck = await emailConfirmationGate();
      if (!emailCheck.allowed) return emailCheck;
      return verificationGate();
    },
    interactionBlocker,                    // <— used by EventsPage to set readOnly
    vip: {
      active: vipActive,
      endsAt: user?.trialEndsAt ?? null,
    },
  };
};